import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Search, Filter, MoreVertical, Mail, User, Shield, AlertTriangle, Crown, AlertCircle } from '../../lib/lucide-proxy';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import { supabase } from '../../config/supabase';
import { USER_ROLES } from '../../config/supabase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const Accounts = () => {
  const { profile } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    phone: '',
    role: USER_ROLES.CUSTOMER,
    password: '',
    brewery_name: '',
    brewery_address: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('users')
        .select(`
          *,
          brewery:breweries(
            id,
            name,
            address,
            phone,
            plan_type,
            is_active
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // FIX: Normalize brewery array to single object
      // Supabase returns an array for relations by default unless .single() is used on the query chain (which isn't possible easily in a nested select)
      const formattedUsers = data.map(user => ({
        ...user,
        brewery: Array.isArray(user.brewery) ? user.brewery[0] : user.brewery
      }));

      setUsers(formattedUsers || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erro ao carregar contas: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => 
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.full_name,
            phone: formData.phone,
            role: formData.role
          }
        }
      });

      if (authError) throw authError;

      const { error: profileError } = await supabase
        .from('users')
        .upsert([{
          id: authData.user.id,
          email: formData.email,
          full_name: formData.full_name,
          phone: formData.phone,
          role: formData.role
        }]);

      if (profileError) throw profileError;

      if (formData.role === USER_ROLES.BREWERY_OWNER && formData.brewery_name) {
        const { error: breweryError } = await supabase
          .from('breweries')
          .insert([{
            owner_id: authData.user.id,
            name: formData.brewery_name,
            slug: formData.brewery_name.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, ''),
            address: formData.brewery_address,
            phone: formData.phone,
            plan_type: 'free',
            is_active: true
          }]);

        if (breweryError) throw breweryError;
      }

      toast.success('Conta criada com sucesso!');
      setIsCreateModalOpen(false);
      fetchUsers();
      resetForm();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      full_name: user.full_name,
      phone: user.phone,
      role: user.role,
      brewery_name: user.brewery?.name || '',
      brewery_address: user.brewery?.address || ''
    });
    setIsEditModalOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await supabase
        .from('users')
        .update({
          full_name: formData.full_name,
          phone: formData.phone,
          role: formData.role
        })
        .eq('id', selectedUser.id);

      if (error) throw error;

      if (selectedUser.brewery && formData.brewery_name) {
        const { error: breweryError } = await supabase
          .from('breweries')
          .update({
            name: formData.brewery_name,
            address: formData.brewery_address
          })
          .eq('id', selectedUser.brewery.id);

        if (breweryError) throw breweryError;
      }

      toast.success('Conta atualizada com sucesso!');
      setIsEditModalOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Erro ao atualizar conta');
    } finally {
      setLoading(false);
    }
  };

  // FEATURE: Grant/Revoke Pro Plan
  const handleTogglePro = async (user) => {
    const brewery = user.brewery;
    
    if (!brewery || !brewery.id) {
      toast.error('Dados da cervejaria inválidos para alteração de plano.');
      return;
    }

    if (!confirm(`Tem certeza que deseja ${brewery.plan_type === 'pro' ? 'REMOVER' : 'CONCEDER'} o plano PRO para esta cervejaria?`)) return;
    
    setActionLoading(true);
    const isUpgrading = brewery.plan_type !== 'pro';
    const newPlan = isUpgrading ? 'pro' : 'free';

    try {
      // 1. Update Brewery Table
      const { error: breweryError } = await supabase
        .from('breweries')
        .update({ plan_type: newPlan })
        .eq('id', brewery.id);

      if (breweryError) throw breweryError;

      // 2. Manage Subscription Record (Manual Override)
      if (isUpgrading) {
        // Fetch PRO plan ID dynamically
        const { data: planData } = await supabase.from('plans').select('id').eq('slug', 'pro').maybeSingle();
        const proPlanId = planData?.id || 2; // Fallback to 2 if not found

        const { error: subError } = await supabase
          .from('subscriptions')
          .upsert([
            {
              brewery_id: brewery.id,
              status: 'active',
              stripe_customer_id: 'manual_admin_grant',
              stripe_subscription_id: `manual_${Date.now()}`,
              plan_id: proPlanId,
              current_period_start: new Date().toISOString(),
              current_period_end: new Date(new Date().setFullYear(new Date().getFullYear() + 100)).toISOString() // Lifetime
            }
          ], { onConflict: 'brewery_id' });

        if (subError) {
          console.warn("Subs error (non-critical):", subError);
        }
      } else {
        // Cancel subscription if downgrading
        const { error: subError } = await supabase
          .from('subscriptions')
          .update({ status: 'canceled' })
          .eq('brewery_id', brewery.id);
        
        if (subError) console.warn("Subs cancel error:", subError);
      }

      toast.success(isUpgrading ? 'Plano PRO concedido!' : 'Plano PRO removido.');
      fetchUsers();
    } catch (error) {
      console.error('Error toggling plan:', error);
      toast.error('Erro ao alterar plano: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      full_name: '',
      phone: '',
      role: USER_ROLES.CUSTOMER,
      password: '',
      brewery_name: '',
      brewery_address: ''
    });
  };

  const getRoleBadge = (role) => {
    const badges = {
      [USER_ROLES.CUSTOMER]: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Cliente' },
      [USER_ROLES.BREWERY_OWNER]: { bg: 'bg-green-100', text: 'text-green-800', label: 'Cervejaria' },
      [USER_ROLES.ADMIN]: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Admin' }
    };
    const badge = badges[role] || badges[USER_ROLES.CUSTOMER];
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>{badge.label}</span>;
  };

  const isAdmin = profile?.role === 'admin';

  return (
    <>
      <Helmet>
        <title>Gerenciar Contas - Cerveja Delivery</title>
      </Helmet>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-dark-900">Gerenciar Contas</h2>
          <Button variant="primary" onClick={() => setIsCreateModalOpen(true)}>
            Nova Conta
          </Button>
        </div>

        {isAdmin && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-start">
            <Crown className="w-5 h-5 text-purple-600 mr-3 mt-0.5" />
            <div className="text-sm text-purple-800">
              <strong>Modo Super Admin:</strong> Você tem permissão para conceder planos PRO manualmente para qualquer cervejaria sem custo.
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input 
                  placeholder="Buscar contas..." 
                  value={searchTerm} 
                  onChange={(e) => setSearchTerm(e.target.value)} 
                  className="pl-10"
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usuário
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Função
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cervejaria
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Criado em
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <User className="w-5 h-5 text-gray-500" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getRoleBadge(user.role)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.brewery ? (
                          <div>
                            <div className="text-sm font-medium text-gray-900">{user.brewery.name}</div>
                            <div className="flex items-center mt-1">
                              <span className={`text-xs px-2 py-0.5 rounded border ${user.brewery.plan_type === 'pro' ? 'bg-purple-100 text-purple-800 border-purple-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                {user.brewery.plan_type === 'pro' ? 'PRO' : 'FREE'}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.brewery ? (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.brewery.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {user.brewery.is_active ? 'Ativo' : 'Inativo'}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(user.created_at).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {/* Admin Action: Toggle Pro Plan */}
                          {isAdmin && user.brewery && (
                            <button 
                              onClick={() => handleTogglePro(user)}
                              disabled={actionLoading}
                              className={`p-1.5 rounded-lg transition-colors ${user.brewery.plan_type === 'pro' ? 'text-red-500 hover:bg-red-50' : 'text-purple-500 hover:bg-purple-50'}`}
                              title={user.brewery.plan_type === 'pro' ? 'Remover Plano Pro' : 'Dar Plano Pro'}
                            >
                              {user.brewery.plan_type === 'pro' ? <AlertCircle className="w-4 h-4" /> : <Crown className="w-4 h-4" />}
                            </button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(user)}>
                            Editar
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {!loading && filteredUsers.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                Nenhuma conta encontrada.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Modal */}
      <Modal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} title="Nova Conta" size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input 
            label="Nome Completo" 
            required 
            value={formData.full_name} 
            onChange={(e) => setFormData({...formData, full_name: e.target.value})} 
          />
          <Input 
            label="Email" 
            type="email" 
            required 
            value={formData.email} 
            onChange={(e) => setFormData({...formData, email: e.target.value})} 
          />
          <Input 
            label="Telefone" 
            value={formData.phone} 
            onChange={(e) => setFormData({...formData, phone: e.target.value})} 
          />
          <Input 
            label="Senha" 
            type="password" 
            required 
            value={formData.password} 
            onChange={(e) => setFormData({...formData, password: e.target.value})} 
          />
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Função</label>
            <select 
              value={formData.role} 
              onChange={(e) => setFormData({...formData, role: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value={USER_ROLES.CUSTOMER}>Cliente</option>
              <option value={USER_ROLES.BREWERY_OWNER}>Cervejaria</option>
              <option value={USER_ROLES.ADMIN}>Admin</option>
            </select>
          </div>

          {formData.role === USER_ROLES.BREWERY_OWNER && (
            <>
              <Input 
                label="Nome da Cervejaria" 
                value={formData.brewery_name} 
                onChange={(e) => setFormData({...formData, brewery_name: e.target.value})} 
              />
              <Input 
                label="Endereço da Cervejaria" 
                value={formData.brewery_address} 
                onChange={(e) => setFormData({...formData, brewery_address: e.target.value})} 
              />
            </>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="secondary" onClick={() => setIsCreateModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" loading={loading}>
              Criar Conta
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Editar Conta" size="lg">
        <form onSubmit={handleUpdate} className="space-y-4">
          <Input 
            label="Nome Completo" 
            required 
            value={formData.full_name} 
            onChange={(e) => setFormData({...formData, full_name: e.target.value})} 
          />
          <Input 
            label="Telefone" 
            value={formData.phone} 
            onChange={(e) => setFormData({...formData, phone: e.target.value})} 
          />
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Função</label>
            <select 
              value={formData.role} 
              onChange={(e) => setFormData({...formData, role: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value={USER_ROLES.CUSTOMER}>Cliente</option>
              <option value={USER_ROLES.BREWERY_OWNER}>Cervejaria</option>
              <option value={USER_ROLES.ADMIN}>Admin</option>
            </select>
          </div>

          {formData.role === USER_ROLES.BREWERY_OWNER && selectedUser?.brewery && (
            <>
              <Input 
                label="Nome da Cervejaria" 
                value={formData.brewery_name} 
                onChange={(e) => setFormData({...formData, brewery_name: e.target.value})} 
              />
              <Input 
                label="Endereço da Cervejaria" 
                value={formData.brewery_address} 
                onChange={(e) => setFormData({...formData, brewery_address: e.target.value})} 
              />
            </>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="secondary" onClick={() => setIsEditModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" loading={loading}>
              Salvar Alterações
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default Accounts;